import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@app/context/SnackbarContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { PaymentProvider } from '@model/PaymentProviderDto'
import type { WalletPaymentProvider } from '@model/WalletPaymentProvider'

const safeErrorPayload = (e: any) => {
  const payload: any = { message: e?.message, name: e?.name }
  const r = e?.response
  if (r) payload.response = { status: r?.status, statusText: r?.statusText, data: r?.data }
  if (e?.data) payload.data = e.data
  if (e?.status) payload.status = e.status
  return payload
}

export const useWithdrawalPaymentProvider = (params: {
  walletPaymentProvider?: WalletPaymentProvider
  walletIdForProvider?: string
  locale: string
  onBack: () => void
}) => {
  const { walletPaymentProvider, walletIdForProvider, locale, onBack } = params
  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const methodId = walletPaymentProvider?.method?.id
  const currencyId = walletPaymentProvider?.currency?.id

  const requestKey = useMemo(() => {
    if (!walletIdForProvider || !methodId) return null
    return `${walletIdForProvider}:${methodId}:${locale}:${currencyId ?? ''}`
  }, [walletIdForProvider, methodId, locale, currencyId])

  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider | undefined>(undefined)
  const [providerLoading, setProviderLoading] = useState(false)

  const activeRequestRef = useRef(0)
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const reqId = ++activeRequestRef.current

    if (!requestKey || !walletPaymentProvider) {
      lastKeyRef.current = null
      setProviderLoading(false)
      setPaymentProvider(undefined)
      return
    }

    const isSameKey = lastKeyRef.current === requestKey
    lastKeyRef.current = requestKey

    setProviderLoading(true)

    const run = async () => {
      try {
        const params = await apiClient.getWalletWithdrawalMethodParameters(
          locale,
          walletIdForProvider as string,
          methodId as string
        )

        if (reqId !== activeRequestRef.current) return

        const next: PaymentProvider = {
          id: methodId as string,
          name: walletPaymentProvider.name,
          description: walletPaymentProvider.description,
          providerCategory: walletPaymentProvider.providerCategory,
          currency: walletPaymentProvider.currency,
          parameters: params,
          logo: walletPaymentProvider.logo,
          method_name: walletPaymentProvider.method_name,
        }

        setPaymentProvider(next)
      } catch (e) {
        if (reqId !== activeRequestRef.current) return
        const payload = safeErrorPayload(e)
        setPaymentProvider(undefined)

        addSnackbar.error({
          message: t('errors.Something went wrong'),
          subTitle: t('errors.Please try again or select a different payment method'),
        })
        console.error('WITHDRAWAL_PROVIDER_PARAMS_FAILED', payload)

        onBack()
      } finally {
        if (reqId === activeRequestRef.current) {
          setProviderLoading(false)
        }
      }
    }

    if (!isSameKey) {
      setPaymentProvider(undefined)
    }

    void run()

    return () => {
      activeRequestRef.current++
    }
  }, [apiClient, locale, methodId, requestKey, walletIdForProvider, walletPaymentProvider])

  return { paymentProvider, providerLoading }
}
